import { Dispatch, SetStateAction, useCallback, useMemo } from 'react'

import { IOptionProps } from '@pnld/components-web'

import {
  IPageInfo,
  IPageParams,
  IUsePaginatedListOptions,
  IUsePaginatedListReturn,
} from './types'

const DEFAULT_PAGE_SIZE = 10
const DEFAULT_PAGE_SIZE_CHOICES: ReadonlyArray<number> = [10, 20, 30, 40, 50]

const toOptions = (values: ReadonlyArray<number>): Array<IOptionProps> =>
  values.map(value => ({ label: String(value), value }))

/**
 * Paginação das listagens: monta as props do `Pagination` e muda a página no
 * estado dos filtros, que continua com quem chama. Sem `pageInfo` (carregando
 * ou com erro) fica na página 1, com os botões desabilitados.
 */
const usePaginatedList = <TFilters extends IPageParams>(
  pageInfo: IPageInfo | undefined,
  setFilters: Dispatch<SetStateAction<TFilters>>,
  {
    pageSizeChoices = DEFAULT_PAGE_SIZE_CHOICES,
    maxPageJumpChoices,
  }: IUsePaginatedListOptions = {}
): IUsePaginatedListReturn<TFilters> => {
  const currentPage = pageInfo?.currentPage || 1
  const pageSize = pageInfo?.pageSize || DEFAULT_PAGE_SIZE
  const count = pageInfo?.count || 0
  const totalPages =
    pageInfo?.totalPages || Math.max(1, Math.ceil(count / pageSize))

  const choicesOfItemsPerPage = useMemo(
    () => toOptions(pageSizeChoices),
    [pageSizeChoices]
  )

  const choicesOfPageJump = useMemo(() => {
    const length = Math.min(totalPages, maxPageJumpChoices ?? totalPages)
    return Array.from({ length }, (_, index) => ({
      label: String(index + 1),
      value: index,
    }))
  }, [totalPages, maxPageJumpChoices])

  const changeFilters = useCallback(
    (changes: Partial<TFilters>) => {
      setFilters(previous => ({ ...previous, ...changes, page: 1 }))
    },
    [setFilters]
  )

  const goToFirstPage = useCallback(() => {
    setFilters(previous =>
      (previous.page || 1) === 1 ? previous : { ...previous, page: 1 }
    )
  }, [setFilters])

  return {
    paginationProps: {
      currentPageIndex: currentPage - 1,
      itemsPerPage: pageSize,
      numberOfItems: count,
      numberOfPages: totalPages,
      choicesOfItemsPerPage,
      choicesOfPageJump,
      onSelectPreviousPage: () => {
        setFilters(previous => ({
          ...previous,
          page: Math.max((previous.page || 1) - 1, 1),
        }))
      },
      onSelectNextPage: () => {
        setFilters(previous => ({
          ...previous,
          page: Math.min((previous.page || 1) + 1, totalPages),
        }))
      },
      onJumpToPage: pageIndex => {
        setFilters(previous => ({ ...previous, page: pageIndex + 1 }))
      },
      onChangeItemsPerPage: itemsPerPage => {
        setFilters(previous => ({
          ...previous,
          pageSize: itemsPerPage,
          page: 1,
        }))
      },
      disableGoToPreviousPage: currentPage <= 1,
      disableGoToNextPage: currentPage >= totalPages,
    },
    changeFilters,
    goToFirstPage,
  }
}

export default usePaginatedList
